import React, { useMemo, useState } from 'react'
import {
  WorkOrderSummary,
  ACTIVE_WORK_ORDER_STATUSES,
  useWorkOrdersQuery,
  FilterInput,
} from '@nyos/shared'
import { useYard } from '../contexts/YardContext'

/**
 * Work-order queue drawer (ADR-005: inspection surface only).
 * Server state comes from useWorkOrdersQuery (ADR-002) — no hand-rolled
 * fetch/polling. The drawer hosts zero workflow logic — new jobs
 * are authored on the /execution/switch-jobs route via top nav.
 */

const POLL_MS = 15_000
const ACTIVE = new Set<string>(ACTIVE_WORK_ORDER_STATUSES)

type Filter = 'ALL' | 'ACTIVE' | 'DONE'

interface SwitchJobsDrawerProps {
  isOpen: boolean
  onClose: () => void
  /** Row to highlight + scroll to (header-search landing). */
  focusId?: string | null
}

function statusColor(status?: string): string {
  switch (status) {
    case 'COMPLETED':
      return 'var(--switch-normal)'
    case 'IN_PROGRESS':
      return 'var(--accent-blue)'
    case 'DISPATCHED':
      return '#ff9800'
    case 'CANCELLED':
    case 'EXPIRED_STALE':
    case 'STALE_CONFLICT':
      return 'var(--status-red)'
    default:
      return 'var(--text-muted)'
  }
}

function statusLabel(status?: string): string {
  switch (status) {
    case 'DISPATCHED': return 'Dispatched'
    case 'IN_PROGRESS': return 'In Progress'
    case 'COMPLETED': return 'Completed'
    case 'CANCELLED': return 'Cancelled'
    case 'EXPIRED_STALE': return 'Expired'
    case 'STALE_CONFLICT': return 'Stale Conflict'
    default: return status ?? 'Unknown'
  }
}

const SwitchJobsDrawer: React.FC<SwitchJobsDrawerProps> = ({ isOpen, onClose, focusId }) => {
  const { activeFacilityId } = useYard()
  const [filter, setFilter] = useState<Filter>('ALL')
  const [search, setSearch] = useState('')

  // Poll while the drawer is open; query is disabled when closed.
  const ordersQuery = useWorkOrdersQuery(activeFacilityId, undefined, {
    enabled: isOpen,
    refetchInterval: POLL_MS,
  })

  const loading = ordersQuery.isPending
  const error = ordersQuery.error instanceof Error ? ordersQuery.error.message : null
  const lastPolledAt = ordersQuery.dataUpdatedAt ? new Date(ordersQuery.dataUpdatedAt) : null

  const orders = useMemo(() => {
    const data = ordersQuery.data ?? []
    // Active first, then terminal — each group sorted newest-ish.
    return [...data].sort((a: WorkOrderSummary, b: WorkOrderSummary) => {
      const aActive = ACTIVE.has(a.status ?? '') ? 0 : 1
      const bActive = ACTIVE.has(b.status ?? '') ? 0 : 1
      if (aActive !== bActive) {
        return aActive - bActive
      }
      const aTs = a.acceptedAt ?? a.completedAt ?? ''
      const bTs = b.acceptedAt ?? b.completedAt ?? ''
      return bTs.localeCompare(aTs)
    })
  }, [ordersQuery.data])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return orders.filter((o) => {
      if (filter === 'ACTIVE' && !ACTIVE.has(o.status ?? '')) {
        return false
      }
      if (filter === 'DONE' && ACTIVE.has(o.status ?? '')) {
        return false
      }
      if (q) {
        const hay = `${o.woNumber ?? ''} ${o.swlNumber ?? ''} ${o.status ?? ''}`.toLowerCase()
        if (!hay.includes(q)) {
          return false
        }
      }
      return true
    })
  }, [orders, filter, search])

  const focusedRowRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (isOpen && focusId && focusedRowRef.current) {
      focusedRowRef.current.scrollIntoView({ block: 'center' })
    }
  }, [isOpen, focusId])

  if (!isOpen) {
    return null
  }

  const filterBtn = (f: Filter, label: string) => (
    <button
      key={f}
      onClick={() => setFilter(f)}
      style={{
        flex: 1,
        padding: '8px',
        borderRadius: '4px',
        border: `1px solid ${filter === f ? 'var(--accent-blue)' : 'var(--border-color)'}`,
        background: filter === f ? 'var(--accent-blue)' : 'transparent',
        color: filter === f ? '#fff' : 'var(--text-primary)',
        fontSize: '12px',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  )

  return (
    <div data-drawer="job-queue" className={`drawer-container ${isOpen ? 'open' : ''}`}>
      <div className="drawer-overlay" onClick={onClose}></div>
      <div className="drawer">
        <button className="drawer-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="inspector-drawer">
          <h2>Job Queue</h2>
          <div style={{ marginBottom: '12px' }}>
            <FilterInput
              aria-label="Filter work orders"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search work orders..."
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {filterBtn('ALL', 'All')}
            {filterBtn('ACTIVE', 'Active')}
            {filterBtn('DONE', 'Completed')}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            {loading
              ? 'Loading…'
              : lastPolledAt
                ? `Updated ${lastPolledAt.toLocaleTimeString()} · polls every ${POLL_MS / 1000}s`
                : ''}
          </div>
          {error && (
            <div style={{ padding: '10px', marginBottom: '12px', borderRadius: '4px', border: '1px solid var(--status-red)', color: 'var(--status-red)', fontSize: '14px' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {visible.map((wo) => {
              const active = ACTIVE.has(wo.status ?? '')
              const focused = focusId != null && (wo.id === focusId || wo.woNumber === focusId)
              return (
                <div
                  key={wo.id}
                  ref={focused ? focusedRowRef : undefined}
                  style={{
                    padding: '12px',
                    borderRadius: '4px',
                    background: 'var(--hover-bg)',
                    border: `1px solid ${focused ? 'var(--accent-blue)' : 'var(--border-color)'}`,
                    boxShadow: focused ? '0 0 0 1px var(--accent-blue)' : undefined,
                    opacity: active ? 1 : 0.65,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                      {wo.woNumber ?? wo.id.slice(0, 8)}
                    </span>
                    <span style={{ fontSize: '12px', color: statusColor(wo.status), fontWeight: 500 }}>
                      {statusLabel(wo.status)}
                    </span>
                  </div>
                  {wo.swlNumber && (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                      SWL {wo.swlNumber}
                    </div>
                  )}
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {wo.acceptedAt && `Accepted ${new Date(wo.acceptedAt).toLocaleTimeString()}`}
                    {wo.completedAt && ` · Completed ${new Date(wo.completedAt).toLocaleTimeString()}`}
                    {wo.cancelledAt && ` · Cancelled ${new Date(wo.cancelledAt).toLocaleTimeString()}`}
                  </div>
                </div>
              )
            })}
            {!loading && visible.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px', padding: '24px 0' }}>
                {orders.length === 0 ? 'No work orders for this yard.' : 'No orders match the filter.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default React.memo(SwitchJobsDrawer)
